import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scrolls to the top on route change — unless the URL carries a hash,
 * in which case it scrolls to that element instead.
 *
 * Previously this always forced scroll-to-top, which silently broke every
 * anchor link on the site (for example /contact#faq).
 */
export default function useScrollToTop() {
    const { pathname, hash, key } = useLocation();

    useEffect(() => {
        if (hash) {
            // The target section may belong to a lazily loaded page that has not
            // painted yet, so retry for a short while before giving up.
            let frames = 0;

            const scrollToAnchor = () => {
                const element = document.querySelector(hash);

                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                    // Move keyboard focus to the target so screen-reader and
                    // keyboard users land where sighted users do.
                    element.setAttribute("tabindex", "-1");
                    element.focus({ preventScroll: true });
                    return;
                }

                if (frames < 30) {
                    frames += 1;
                    requestAnimationFrame(scrollToAnchor);
                }
            };

            requestAnimationFrame(scrollToAnchor);
            return;
        }

        window.scrollTo({ top: 0, behavior: "instant" });
        // `key` changes on every navigation, including a hash-only change on
        // the page you are already viewing. Without it, clicking "Get a Free
        // Quote" from the homepage would update the URL but never scroll.
    }, [pathname, hash, key]);

    return null;
}
